import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { faCalendar, faCaretDown, faCaretRight, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal, NgbCalendar, NgbDateParserFormatter, NgbModal, NgbInputDatepicker, NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../alert.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { Payment } from '../payment';
import { PaymentRefundModalComponent } from '../payment-refund-modal/payment-refund-modal.component';
import { PaymentService } from '../payment.service';
import { SendEmailComponent } from '../send-email/send-email.component';
import { LoaderService } from '@app-shared/services/loader.service';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { RouterLink } from '@angular/router';
import { NgIf, NgFor, CommonModule } from '@angular/common';

@Component({
  selector: 'app-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: ['./payment-modal.component.css'],
  imports: [ReactiveFormsModule, NgIf, RouterLink, FaIconComponent, NgbInputDatepicker, NgFor, NgbCollapse, NgxSpinnerComponent, CommonModule]
})
export class PaymentModalComponent implements OnInit {

  // Properties
  @Input() invoiceId: string;
  @Input() invoiceNumber: number;
  @Input() clientId: string;
  @Input() payment: Payment;
  @Input() invoiceTotal: number = 0;
  @Input() totalDebitAmount: number = 0;
  @Input() projectId: string;
  @Input() showInvoiceLink: boolean = false;
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();
  showPaymentEmail: boolean = false;
  paymentForm: UntypedFormGroup;
  transactionDetailsAreCollapsed: boolean = true;
  statusList: Array<{ label: string, value: string }> = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Closed', value: 'CLOSED' },
    { label: 'Returned', value: 'RETURNED' },
  ];
  savePaymentButtonDisabled: boolean = false;

  // Font Awesome Properties
  faCalendar = faCalendar;
  faCaretRight = faCaretRight;
  faCaretDown = faCaretDown;
  faExternalLinkAlt = faExternalLinkAlt;

  constructor(private paymentService: PaymentService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private calendar: NgbCalendar,
    private dateParserFormatter: NgbDateParserFormatter,
    public modal: NgbActiveModal,
    private loaderService: LoaderService) { }

  ngOnInit(): void {
    this.paymentForm = new UntypedFormGroup({
      amount: new UntypedFormControl(),
      date: new UntypedFormControl(this.calendar.getToday()),
      category: new UntypedFormControl('CARD'),
      status: new UntypedFormControl('CLOSED'),
      note: new UntypedFormControl(),
      createCreditNote: new UntypedFormControl(true)
    });
    if (this.payment) this.preparePaymentForm();
  }

  // Open Delete Payment Confirmation Modal
  openDeletePaymentConfirmationModal(): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this payment?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.deletePayment();
    });
  }

  // Open Send Email Modal For Payment
  private openSendEmailModalForPayment(paymentId: string = null): void {
    const modalRef = this.modalService.open(SendEmailComponent);
    modalRef.componentInstance.clientId = (this.payment) ? this.payment.clientId : this.clientId;
    modalRef.componentInstance.invoiceId = (this.payment) ? this.payment.invoiceId : this.invoiceId;
    modalRef.componentInstance.paymentId = (this.payment) ? this.payment.id : paymentId;
    modalRef.componentInstance.projectId = this.projectId
    modalRef.componentInstance.type = 'PAYMENT';
    modalRef.componentInstance.subject = `Receipt for Invoice #${this.invoiceNumber}`;
  }

  // Open Payment Refund Modal
  openPaymentRefundModal(): void {
    const modalRef = this.modalService.open(PaymentRefundModalComponent);
    modalRef.componentInstance.payment = this.payment;
    modalRef.componentInstance.saved.subscribe(() => {
      this.saved.emit();
      this.modal.close();
    });
  }

  // Prepare Payment Form
  private preparePaymentForm(): void {
    this.paymentForm.controls.amount.setValue(this.payment.amount.toFixed(2));
    this.paymentForm.controls.date.setValue(this.dateParserFormatter.parse(this.payment.date));
    this.paymentForm.controls.category.setValue(this.payment.category);
    this.paymentForm.controls.status.setValue(this.payment.status);
    this.paymentForm.controls.note.setValue(this.payment.note);
    if (this.payment.isExternal) {
      this.paymentForm.controls.amount.disable();
      this.paymentForm.controls.date.disable();
      this.paymentForm.controls.category.disable();
    }
    if (this.payment.category == 'SHORT_PAYMENT') {
      this.paymentForm.controls['status'].setValue('CLOSED');
      this.paymentForm.controls['status'].disable();
    }
    if (this.payment.category === 'CREDIT NOTE') {
      this.paymentForm.disable();
      setTimeout(() => {
        this.paymentForm.controls.category.setValue(this.payment.category);
      }, 100);
    }
  }
  // Save Payment
  savePayment(): void {
    this.savePaymentButtonDisabled = true;
    this.loaderService.showSpinner();
    if (this.payment) this.updatePayment();
    else this.addPayment();
  }

  // Add Payment
  private addPayment(): void {
    if (this.paymentForm.valid) {
      const paymentType: string = this.paymentForm.value.category == 'SHORT_PAYMENT' ? 'SHORT_PAYMENT' : 'DEBIT';
      const paymentStatus: string = this.paymentForm.value.category == 'SHORT_PAYMENT' ? 'CLOSED' : this.paymentForm.value.status;
      const payment: any = {
        invoiceId: this.invoiceId,
        amount: this.paymentForm.value.amount,
        date: this.dateParserFormatter.format(this.paymentForm.value.date),
        type: paymentType,
        category: this.paymentForm.value.category,
        status: paymentStatus,
        note: this.paymentForm.value.note,
        createCreditNote: this.paymentForm.value.createCreditNote
      };
      this.paymentService.addPayment(payment).subscribe(
        {
          next: (paymentId) => {
            this.alertService.showSuccessAlert('Payment Added');
            this.saved.emit();
            this.modal.close();
            this.hideSpinnerAndResetSaveBtnDisabled();
            if (this.showPaymentEmail) this.openSendEmailModalForPayment(paymentId);
          },
          error: (error) => {
            if (error) this.hideSpinnerAndResetSaveBtnDisabled();
          }
        }
      );
    }
  }

  // Update Payment
  private updatePayment(): void {
    if (this.paymentForm.valid) {
      const formData = this.payment?.isExternal
        ? this.paymentForm.getRawValue()
        : this.paymentForm.value;
      const paymentStatus: string = formData.category == 'SHORT_PAYMENT' ? 'CLOSED' : formData.status;
      const payment: any = {
        id: this.payment.id,
        amount: formData.amount,
        date: this.dateParserFormatter.format(formData.date),
        category: formData.category,
        status: paymentStatus,
        note: formData.note
      };
      this.paymentService.updatePayment(payment).subscribe(
        {
          next: () => {
            this.alertService.showSuccessAlert('Payment Updated');
            this.saved.emit();
            this.modal.close();
            this.hideSpinnerAndResetSaveBtnDisabled();
            if (this.showPaymentEmail) this.openSendEmailModalForPayment();
          },
          error: (error) => {
            if (error) this.hideSpinnerAndResetSaveBtnDisabled();
          }
        }
      );
    }
  }

  hideSpinnerAndResetSaveBtnDisabled() {
    this.savePaymentButtonDisabled = false;
    this.loaderService.hideSpinner(200);
  }

  // Delete Payment
  deletePayment(): void {
    this.paymentService.deletePayment(this.payment.id).subscribe(() => {
      this.alertService.showSuccessAlert('Payment Deleted');
      this.saved.emit();
      this.modal.close();
    });
  }

  // Toggle Show Payment Email
  toggleShowPaymentEmail(show: boolean): void {
    this.showPaymentEmail = show;
  }

  // Category Selected
  categorySelected(category: string): void {
    if (category === 'ACH') {
      this.statusList = [
        { label: 'Pending', value: 'APPROVED' },
        { label: 'Settled', value: 'SETTLED' },
        { label: 'Closed', value: 'CLOSED' },
        { label: 'Returned', value: 'RETURNED' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Voided', value: 'VOIDED' },
        { label: 'Rejected', value: 'REJECTED' },
        { label: 'Notice of Change', value: 'NOTICE OF CHANGE' }
      ];
      this.paymentForm.controls['status'].enable();
    } else {
      this.statusList = [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Rejected', value: 'REJECTED' },
        { label: 'Closed', value: 'CLOSED' }
      ];
      if (category === 'SHORT_PAYMENT') {
        setTimeout(() => {
          this.paymentForm.controls['status'].setValue('CLOSED');
          this.paymentForm.controls['status'].disable();
        }, 50);
      } else {
        this.paymentForm.controls['status'].enable();
      }
    }
  }

  get creditNoteAmount(): number {
    let amount = Number(this.invoiceTotal || 0) - Number(this.totalDebitAmount || 0);
    if (+this.amount.value > amount) {
      return +((+this.amount.value - amount).toFixed(2));
    }
    return 0;
  }

  // Payment Form Accessors
  get amount() { return this.paymentForm.controls.amount; }
  get date() { return this.paymentForm.controls.date; }
  get category() { return this.paymentForm.controls.category; }
  get status() { return this.paymentForm.controls.status; }
  get note() { return this.paymentForm.controls.note; }
  get createCreditNote() { return this.paymentForm.controls.createCreditNote; }
}
